import React, { FC, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { getAllStyle } from '../../stores/reducers/sections';
import { LightenDarkenColor, hexToRGB } from '../../helpers/colors';
import { getContentDataRegistration } from '../../stores/reducers/userData';

const InlineStyle: FC = (): ReactElement => {
  const style = useSelector(getAllStyle);
  const breakpoints = {
    mobiles: {
      width: 375,
      fontSizeHead: Math.round(style.textSize.header * 0.43),
      lineHeightHead: Math.round(style.textSize.header * 0.43 * 1.33),
    },
    sm: {
      width: 576,
      fontSizeHead: Math.round(style.textSize.header * 0.544),
      lineHeightHead: Math.round(style.textSize.header * 0.544 * 1.26),
    },
    md: {
      width: 768,
      fontSizeHead: Math.round(style.textSize.header * 0.658),
      lineHeightHead: Math.round(style.textSize.header * 0.658 * 1.19),
    },
    lg: {
      width: 992,
      fontSizeHead: Math.round(style.textSize.header * 0.772),
      lineHeightHead: Math.round(style.textSize.header * 0.772 * 1.13),
    },
    xl: {
      width: 1200,
      fontSizeHead: Math.round(style.textSize.header * 0.886),
      lineHeightHead: Math.round(style.textSize.header * 0.886 * 1.06),
    },
    hd: {
      width: 1440,
      fontSizeHead: style.textSize.header,
      lineHeightHead: 1,
    },
  };

  const headerBackgroudColor = (bgBehavior: string): string => {
    let result = '';
    if (bgBehavior === 'transparent') {
      result = 'rgba(0,0,0,0)!important';
    } else if (bgBehavior === 'visible') {
      result = style.colors.header + '!important';
    } else if (bgBehavior === 'default') {
      result = style.colors.header;
    }
     return result;
  }

  const { backendURL, internalImage } = useSelector(getContentDataRegistration);
  const css = `
    :root {
      --colorPrimary: ${style.colors.primary};
      --colorExtra: ${style.colors.accent};
      --colorPink: ${style.colors.extra};
      --colorWhite: ${style.colors.white};
      --colorFont: ${style.colors.font};
      --colorHeader: ${style.colors.header};
      --colorFooter: ${style.colors.footer};
      --colorHeaderLink: ${style.colors.colorHeaderLink};

      --colorPrimaryText: ${LightenDarkenColor(style.colors.primary, 10)};
      --colorPrimaryHover: ${LightenDarkenColor(style.colors.primary, 5)};
      --colorGreen: #27AE60;
      --colorRed: #FE3737;
      --colorDarkGray: ${hexToRGB(style.colors.font, 0.75)};
      --colorLightGray: ${hexToRGB(style.colors.font, 0.38)};
      --colorWhite: #fff;
      --borderColor: ${hexToRGB('#000000', 0.12)};
      --bgInput: rgba(31, 32, 65, .05);

      --playerDesktopSize: ${style.playerSettings.playerWidth}%;
      --playerMobileSize: ${style.playerSettings.playerMobileWidth}%;
    }
    body, html, .btn, .ui-input, button {
      font-family: "${style.fonts.main}";
    }
    h1, h2, h3, h4, .section-title {
      font-family: "${style.fonts.headings}";
    }
    .f-heading {
      font-family: "${style.fonts.headings}";
    }
    .f-main {
      font-family: "${style.fonts.main}";
    }
    .inner-section, .schedule-info-line {
      border-top-left-radius: ${style.roundings.big}px;
      border-top-right-radius: ${style.roundings.big}px;
    }
    .ui-input, .st-slider-item, .btn, .schedule-item, .speaker-item__photo, .speaker__photo, .all-speakers-btn, .schedule-dates li span {
      border-radius: ${style.roundings.small}px;
    }
    .schedule-dates li:first-child {
      border-top-left-radius: ${style.roundings.small}px;
      border-bottom-left-radius: ${style.roundings.small}px;
    }
    .schedule-dates li:last-child {
      border-top-right-radius: ${style.roundings.small}px;
      border-bottom-right-radius: ${style.roundings.small}px;
    }
    .r-big {
      border-radius: ${style.roundings.big}px;
    }
    .r-big-tl {
      border-top-left-radius: ${style.roundings.big}px;
    }
    .r-big-tr {
      border-top-right-radius: ${style.roundings.big}px;
    }
    .r-big-br {
      border-bottom-right-radius: ${style.roundings.big}px;
    }
    .r-big-bl {
      border-bottom-left-radius: ${style.roundings.big}px;
    }
    .r-small {
      border-radius: ${style.roundings.small}px;
    }
    .r-small-tl {
      border-top-left-radius: ${style.roundings.small}px;
    }
    .r-small-tr {
      border-top-right-radius: ${style.roundings.small}px;
    }
    .r-small-br {
      border-bottom-right-radius: ${style.roundings.small}px;
    }
    .r-small-bl {
      border-bottom-left-radius: ${style.roundings.small}px;
    }
    .c-primary {
      color: ${style.colors.primary};
    }
    .c-accent {
      color: ${style.colors.accent};
    }
    .c-extra {
      color: ${style.colors.extra};
    }
    .c-white {
      color: ${style.colors.white};
    }
    .c-font {
      color: ${style.colors.font};
    }
    .bg-primary {
      background-color: ${style.colors.primary};
    }
    .bg-accent {
      background-color: ${style.colors.accent};
    }
    .bg-extra {
      background-color: ${style.colors.extra};
    }
    .bg-white {
      background-color: ${style.colors.white};
    }
    .bg-font {
      background-color: ${style.colors.font};
    }
    .header-optional {
      background-color: ${headerBackgroudColor(style.header.bgBehavior)};
    }
    .header-gradient {
      background: ${style.header.hasHeaderGradient && 'linear-gradient(180deg, rgba(12, 13, 24, 0.5) -93.23%, rgba(31, 32, 65, 0) 100%)'}
    }
    .header-overlay {
      background-color: ${style.colors.header};
    }
    .header-link-custom-color: {
      color: ${style.header.menuFontColor}
    }
    .bg-header, .mobile-logo {
      background-color: ${style.colors.header};
    }
    .bg-header--ontop, .mobile-logo--ontop {
      background-color: rgba(0,0,0,0);
    }
    .bg-header--sticky, .mobile-logo--sticky {
      background-color: ${style.colors.header};
    }
    .mobile-logo {
      background-color: ${Number(style.header.opacity) === 1 ? style.colors.header : 'rgba(0,0,0,0)'};
    }
    .header-demozone {
      background-color: ${style.colors.header};
    }
    .header-first-button {
      background-color: ${style.header.firstButtonColor ? style.header.firstButtonColor : style.colors.primary};
    }
    .header-first-button.opacity {
      background-color: ${style.header.firstButtonOpacity === 0 && 'rgba(0,0,0,0)'};
    }
    .header-first-button.gradient::before {
      background: ${style.header.firstButtonGradientActive === 0 ? 'transparent' : 'linear-gradient(180deg, rgba(12, 13, 24, 0.5) -93.23%, rgba(31, 32, 65, 0) 100%)'};
    }
    .header-first-button a {
      color: ${style.header.firstButtonFontColor ? style.header.firstButtonFontColor : style.colors.white} !important;
    }

    .header-second-button {
      background-color: ${style.header.secondButtonColor ? style.header.secondButtonColor : style.colors.primary};
    }
    .header-second-button.opacity {
      background-color: ${style.header.secondButtonOpacity === 0 && 'rgba(0,0,0,0)'};
    }
    .header-second-button.gradient::before {
      background: ${style.header.secondButtonGradientActive === 0 ? 'transparent' : 'linear-gradient(180deg, rgba(12, 13, 24, 0.5) -93.23%, rgba(31, 32, 65, 0) 100%)'};
    }
    .header-second-button a {
      color: ${style.header.secondButtonFontColor ? style.header.secondButtonFontColor : style.colors.white} !important;
    }

    .header-third-button {
      background-color: ${style.header.thirdButtonColor ? style.header.thirdButtonColor : style.colors.primary};
    }
    .header-third-button.opacity {
      background-color: ${style.header.thirdButtonOpacity === 0 && 'rgba(0,0,0,0)'};
    }
    .header-third-button.gradient::before {
      background: ${style.header.thirdButtonGradientActive === 0 ? 'transparent' : 'linear-gradient(180deg, rgba(12, 13, 24, 0.5) -93.23%, rgba(31, 32, 65, 0) 100%)'};
    }
    .header-third-button a {
      color: ${style.header.thirdButtonFontColor ? style.header.thirdButtonFontColor : style.colors.white} !important;
    }

    .link-exhibition {
      background-color: #9e41a8;
    }
    .bg-header .link-live {
      background-color: ${style.colors.primary};
    }
    .header-min-nav--item.additional__link.link-live {
      background-color: ${style.colors.primary};
    }
    .bg-counter {
      background-color: ${style.colors.counter};
    }
    .bg-footer {
      background-color: ${style.colors.footer};
    }
    .section-title, .fz-header, h1 {
      font-size: ${style.textSize.header}px;
      line-height: 1 !important;
    }
    .zonedata-item__btn {
      background-color: #FDFAF2;
      border-radius: ${style.roundings.small}px;
      font-size: 17px;
      line-height: 1.2;
      color: ${style.colors.font};
      padding: 10px 20px;
      margin-top: 68px;
    }
    h2 {
      font-size: ${Math.round(style.textSize.header * 0.36)}px !important;
    }
    @media only screen and (max-width: ${breakpoints.xl.width}px) {
      .section-title, .fz-header, h1 {
        font-size: ${breakpoints.xl.fontSizeHead}px !important;
        line-height: ${breakpoints.xl.lineHeightHead}px !important;
      }
      h2 {
        font-size: ${Math.round(breakpoints.xl.fontSizeHead * 0.36)}px !important;
      }
    }
    @media only screen and (max-width: ${breakpoints.lg.width}px) {
      .section-title, .fz-header, h1 {
        font-size: ${breakpoints.lg.fontSizeHead}px !important;
        line-height: ${breakpoints.lg.lineHeightHead}px !important;
      }
    }
    @media only screen and (max-width: ${breakpoints.md.width}px) {
      .section-title, .fz-header, h1 {
        font-size: ${breakpoints.md.fontSizeHead}px !important;
        line-height: ${breakpoints.md.lineHeightHead}px !important;
      }
    }
    @media only screen and (max-width: ${breakpoints.sm.width}px) {
      .section-title, .fz-header, h1 {
        font-size: ${breakpoints.sm.fontSizeHead}px !important;
        line-height: ${breakpoints.sm.lineHeightHead}px !important;
      }
    }
    @media only screen and (max-width: ${breakpoints.mobiles.width}px) {
      .section-title, .fz-header, h1 {
        font-size: ${breakpoints.mobiles.fontSizeHead}px !important;
        line-height: ${breakpoints.mobiles.lineHeightHead}px !important;
      }
    }
    .user-fs-text {
      font-size: ${style.textSize.body}px;
      line-height: 1.5 !important;
    }
    .user-fs-text__sm {
      font-size: ${Math.round(style.textSize.body * 0.8)}px !important;
      line-height: 1.4 !important;
    }
    strong {
      font-size: ${Math.round(style.textSize.body * 1.5)}px !important;
      line-height: 1.4 !important;
    }
    .static-page-section .inner-section__head {
      background-image: url('${backendURL}${internalImage}');
    }
    .fill-accent * {
      fill: ${style.colors.accent};
    }
    .header__menu li,
    .additional__link,
    .additional_button button,
    .additional_button a {
      font-size: ${style.textSize.menu}px;
      text-transform: ${style.textSize.capitalizeMenuLink ? 'uppercase' : 'initial'};
    }
  `;

  return <style>{css}</style>;
};

export default InlineStyle;
